// Signage → Temas (/signage/temas). Brand kit por empresa: paleta (tokens) +
// logo + preset base, con vista previa en vivo del reproductor.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Palette, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls } from '@/features/pos/shared';
import { resizeToDataURL } from '@/features/inventario/shared';
import { SIG, sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';
import { SignagePlayer, playerCss } from './player';
import type { Categoria, TemaTokens } from './player';
import { apiClient } from '@/lib/api/client';

const CAMPOS: { k: keyof TemaTokens; l: string }[] = [
  { k: 'paper', l: 'Fondo' }, { k: 'paper2', l: 'Fondo 2' }, { k: 'ink', l: 'Tinta' }, { k: 'soft', l: 'Tinta suave' },
  { k: 'accent', l: 'Acento' }, { k: 'sage', l: 'Disponible' }, { k: 'oxido', l: 'Últimas unid.' }, { k: 'topo', l: 'Agotado' },
];
const PRESETS: Record<string, TemaTokens> = {
  calido: { paper: '#efe7da', paper2: '#e7ddcc', ink: '#191310', soft: '#6f655a', accent: '#c6303a', sage: '#4f7a52', oxido: '#b0552a', topo: '#8a8175' },
  frio: { paper: '#eceae3', paper2: '#e3e1d8', ink: '#15181b', soft: '#6b6f76', accent: '#2b3a67', sage: '#5b7a5b', oxido: '#a6564b', topo: '#8a8175' },
  oscuro: { paper: '#0e0f13', paper2: '#171a22', ink: '#f2eee4', soft: '#9aa0aa', accent: '#c9a86a', sage: '#6cc08c', oxido: '#e0a24a', topo: '#79736a' },
};
const SAMPLE: Categoria[] = [{
  categoria: 'Destacados', n: 3, productos: [
    { id: 's1', nombre: 'Producto estrella', descripcion: null, marca: 'Marca', precio: 12.9, iva_tarifa: 15, controla_stock: true, stock: 3, agotado: false, imagen: null },
    { id: 's2', nombre: 'Otro producto', descripcion: null, marca: null, precio: 8.5, iva_tarifa: 15, controla_stock: false, stock: null, agotado: false, imagen: null },
    { id: 's3', nombre: 'Sin stock', descripcion: null, marca: null, precio: 5, iva_tarifa: 0, controla_stock: true, stock: 0, agotado: true, imagen: null },
  ],
}];

interface Tema { id: string; codigo: string | null; nombre: string; preset: string; tokens: TemaTokens; logo: string | null; es_default: boolean; es_activo: boolean }

export function Temas() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Tema> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { data: temas = [] } = useQuery<Tema[]>({
    queryKey: ['sig-temas-full'], queryFn: () => sigList<Tema>('marca_tema?select=*&deleted_at=is.null&order=nombre'),
  });
  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['sig-temas-full'] }); void qc.invalidateQueries({ queryKey: ['sig-temas'] }); };

  const setTok = (k: keyof TemaTokens, v: string) => setEdit((e) => ({ ...e, tokens: { ...(e?.tokens ?? {}), [k]: v } }));

  const subirLogo = async (file: File | undefined) => {
    if (!file) return;
    try { const logo = await resizeToDataURL(file, 400, 0.85); setEdit((e) => ({ ...e, logo })); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const guardar = async () => {
    if (!edit?.nombre) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      const body = {
        nombre: edit.nombre, codigo: edit.codigo || null, preset: edit.preset || 'custom',
        tokens: edit.tokens ?? {}, logo: edit.logo ?? null, es_default: edit.es_default ?? false, es_activo: edit.es_activo ?? true,
      };
      // exclusividad del default: si este es default, apaga los demás primero
      if (body.es_default) await apiClient.patch('/rest/signage.marca_tema?es_default=eq.true', { es_default: false }, SIG);
      if (edit.id) await sigUpdate('marca_tema', edit.id, body); else await sigInsert('marca_tema', body);
      setEdit(null); invalidar(); toast.success('Tema guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const previewData = useMemo(() => ({
    empresa: 'Vista previa', local: 'Tema',
    tema: { preset: edit?.preset ?? 'calido', tokens: edit?.tokens ?? PRESETS.calido },
    escenas: [{ tipo: 'catalogo', dur: 6 }], categorias: SAMPLE,
  }), [edit?.preset, edit?.tokens]);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <style>{playerCss}</style>
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Palette className="w-5 h-5" /> Temas (brand kit)</h1>
        <Button className="ml-auto" onClick={() => setEdit({ preset: 'calido', tokens: PRESETS.calido, es_activo: true })}><Plus className="w-4 h-4 mr-1" /> Nuevo tema</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {temas.map((t) => (
          <div key={t.id} className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-medium">{t.nombre}</span>
              {t.es_default && <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Star className="w-3.5 h-3.5" /> Por defecto</span>}
              <div className="ml-auto flex">
                <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(t)}><Pencil className="w-4 h-4" /></button>
                <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('marca_tema', t.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
            <div className="flex gap-1">
              {CAMPOS.map(({ k }) => <span key={k} className="flex-1 h-6 rounded" style={{ background: t.tokens?.[k] ?? '#ccc' }} title={k} />)}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{t.preset}</div>
          </div>
        ))}
      </div>

      {edit && (
        <PanelIntegrado title={edit.id ? 'Editar tema' : 'Nuevo tema'} onClose={() => setEdit(null)} wide>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
                <Campo label="Preset base">
                  <select className={fieldCls} value={edit.preset ?? 'custom'}
                          onChange={(e) => { const p = e.target.value; setEdit({ ...edit, preset: p, tokens: PRESETS[p] ?? edit.tokens }); }}>
                    <option value="calido">Cálido</option><option value="frio">Frío</option><option value="oscuro">Oscuro</option><option value="custom">Personalizado</option>
                  </select>
                </Campo>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {CAMPOS.map(({ k, l }) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="color" value={edit.tokens?.[k] ?? '#000000'} onChange={(e) => setTok(k, e.target.value)} className="w-8 h-8 rounded border" />
                    <span className="text-muted-foreground">{l}</span>
                  </label>
                ))}
              </div>
              <Campo label="Logo (opcional)">
                <div className="flex items-center gap-2">
                  {edit.logo && <img src={edit.logo} alt="logo" className="h-8 rounded border bg-white/50" />}
                  <input type="file" accept="image/*" onChange={(e) => { void subirLogo(e.target.files?.[0]); e.target.value = ''; }} className="text-sm" />
                  {edit.logo && <button className="text-xs text-red-500" onClick={() => setEdit({ ...edit, logo: null })}>quitar</button>}
                </div>
              </Campo>
              <Switch checked={edit.es_default ?? false} onChange={(v) => setEdit({ ...edit, es_default: v })} label="Tema por defecto de la empresa" />
              <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Vista previa</div>
              <div className="aspect-video rounded-lg overflow-hidden border"><SignagePlayer data={previewData} /></div>
            </div>
          </div>
        </PanelIntegrado>
      )}
    </div>
  );
}
